import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Loader2, ArrowLeft, Paperclip, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "spine", label: "Spine-Related Concern" },
  { id: "brain", label: "Brain-Related Concern" },
  { id: "followup", label: "Follow-up / Second Opinion" },
  { id: "camp", label: "Free Community Camp" },
];

const AREA_MAP = {
  spine: [
    { id: "neck", label: "Neck / Cervical Pain" },
    { id: "lowerback", label: "Lower Back / Leg Pain" },
    { id: "recovery", label: "Post-Surgery Recovery" },
  ],
  brain: [
    { id: "headache", label: "Headaches / Neurological Symptoms" },
    { id: "tumor", label: "Suspected Tumor" },
    { id: "trauma", label: "Head Injury / Trauma" },
  ],
  followup: [
    { id: "review", label: "Reviewing Previous Treatment" },
    { id: "secondopinion", label: "Second Opinion on Existing Diagnosis" },
  ],
  camp: [
    { id: "nextdate", label: "Next Camp Date & Location" },
    { id: "eligibility", label: "Who Can Attend" },
  ],
};

const SPECIFIC_MAP = {
  neck: ["ACDF", "Lateral Mass Fixation", "Not sure — general evaluation"],
  lowerback: ["Endoscopic Discectomy", "TLIF", "Not sure — general evaluation"],
  recovery: ["Follow-up after Endoscopic Discectomy", "Follow-up after TLIF / ACDF", "General post-op check"],
  headache: ["General Neurosurgery evaluation"],
  tumor: ["Brain Tumor Surgery evaluation"],
  trauma: ["General Neurosurgery evaluation"],
  review: ["Review existing reports/imaging"],
  secondopinion: ["Second opinion consultation"],
  nextdate: ["Just send me the details"],
  eligibility: ["Just send me the details"],
};

const StepShell = ({ step, total, title, onBack, children }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <span className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#5ECCA0]">
        Step {step} of {total}
      </span>
      {onBack && (
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-white/50 hover:text-[#5ECCA0] transition-colors">
          <ArrowLeft size={13} /> Back
        </button>
      )}
    </div>
    <div className="h-1 bg-white/10 rounded-full mb-7 overflow-hidden">
      <div className="h-full bg-[#5ECCA0] rounded-full transition-all duration-300" style={{ width: `${(step / total) * 100}%` }} />
    </div>
    <h3 className="text-white text-lg md:text-xl font-medium mb-5">{title}</h3>
    {children}
  </div>
);

const ChoiceBtn = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left rounded-2xl border px-5 py-4 mb-3 transition-colors ${
      active
        ? "border-[#5ECCA0] bg-[#5ECCA0]/10 text-white"
        : "border-white/15 bg-[#14263D] text-white/80 hover:border-white/30"
    }`}
  >
    {children}
  </button>
);

const BookingWizard = () => {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("");
  const [area, setArea] = useState("");
  const [specific, setSpecific] = useState("");
  const [appointmentType, setAppointmentType] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", note: "" });
  const [loading, setLoading] = useState(false);

  const areaOptions = AREA_MAP[category] || [];
  const specificOptions = SPECIFIC_MAP[area] || [];
  const [documentBase64, setDocumentBase64] = useState(null);
  const [documentName, setDocumentName] = useState("");

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [preferredDay, setPreferredDay] = useState("");

  useEffect(() => {
    axios.get(`${API}/clinic-locations`).then((res) => {
      setLocations(res.data);
      if (res.data.length === 1) setLocationId(res.data[0].id);
    }).catch(() => {});
  }, []);

  const selectedLocation = locations.find((l) => l.id === locationId);

  const onDocumentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setDocumentBase64(reader.result);
      setDocumentName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const clearDocument = () => {
    setDocumentBase64(null);
    setDocumentName("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !appointmentType) {
      toast.error("Please add your name, phone, and preferred appointment type.");
      return;
    }
    setLoading(true);
    const categoryLabel = CATEGORIES.find((c) => c.id === category)?.label || "";
    const areaLabel = areaOptions.find((a) => a.id === area)?.label || "";
    const reason = [categoryLabel, areaLabel, specific].filter(Boolean).join(" — ");
    const note = [
      `Appointment type: ${appointmentType}`,
      selectedLocation ? `Location: ${selectedLocation.clinic_name} — ${selectedLocation.city}` : "",
      preferredDay ? `Preferred day: ${preferredDay}` : "",
      form.note ? `Note: ${form.note}` : "",
    ].filter(Boolean).join(" | ");

    try {
      await axios.post(`${API}/appointments`, {
        name: form.name, phone: form.phone, reason, note, document_base64: documentBase64 || "",
      });
      toast.success("Request received. Dr. Chanduka's team will be in touch to confirm.");
      setStep(1); setCategory(""); setArea(""); setSpecific(""); setAppointmentType("");
      setForm({ name: "", phone: "", note: "" });
      setLocationId(locations.length === 1 ? locations[0].id : "");
      setPreferredDay("");
      clearDocument();
    } catch (err) {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#1B3352] border border-white/10 rounded-3xl p-7 md:p-9" data-testid="booking-wizard">
      {step === 1 && (
        <StepShell step={1} total={4} title="What can we help you with?">
          {CATEGORIES.map((c) => (
            <ChoiceBtn key={c.id} testId={`wizard-cat-${c.id}`} active={category === c.id}
              onClick={() => { setCategory(c.id); setArea(""); setSpecific(""); setStep(2); }}>
              {c.label}
            </ChoiceBtn>
          ))}
        </StepShell>
      )}

      {step === 2 && (
        <StepShell step={2} total={4} title="Which area interests you?" onBack={() => setStep(1)}>
          {areaOptions.map((a) => (
            <ChoiceBtn key={a.id} testId={`wizard-area-${a.id}`} active={area === a.id}
              onClick={() => { setArea(a.id); setSpecific(""); setStep(3); }}>
              {a.label}
            </ChoiceBtn>
          ))}
        </StepShell>
      )}

      {step === 3 && (
        <StepShell step={3} total={4} title="Which part exactly?" onBack={() => setStep(2)}>
          {specificOptions.map((s) => (
            <ChoiceBtn key={s} testId={`wizard-specific-${s.replace(/\s/g, "-").toLowerCase()}`} active={specific === s}
              onClick={() => { setSpecific(s); setStep(4); }}>
              {s}
            </ChoiceBtn>
          ))}
        </StepShell>
      )}

      {step === 4 && (
        <StepShell step={4} total={4} title="Book your consultation" onBack={() => setStep(3)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <ChoiceBtn testId="wizard-type-video" active={appointmentType === "Video consultation"}
              onClick={() => setAppointmentType("Video consultation")}>
              <span className="font-medium">Video Consultation</span>
              <div className="text-xs text-white/50 mt-1">Quick first review on a call</div>
            </ChoiceBtn>
            <ChoiceBtn testId="wizard-type-inperson" active={appointmentType === "In-person visit"}
              onClick={() => setAppointmentType("In-person visit")}>
              <span className="font-medium">In-Person Visit</span>
              <div className="text-xs text-white/50 mt-1">Visit the clinic directly</div>
            </ChoiceBtn>
          </div>

          {locations.length > 0 && (
            <div className="mb-5">
              <Label className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#5ECCA0] block mb-2">
                Choose a location
              </Label>
              {locations.map((loc) => (
                <ChoiceBtn key={loc.id} testId={`wizard-location-${loc.id}`} active={locationId === loc.id}
                  onClick={() => { setLocationId(loc.id); setPreferredDay(""); }}>
                  <span className="font-medium">{loc.clinic_name}</span>
                  <div className="text-xs text-white/50 mt-1">{loc.city}</div>
                </ChoiceBtn>
              ))}
            </div>
          )}

          {selectedLocation && selectedLocation.available_days?.length > 0 && (
            <div className="mb-5">
              <Label className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#5ECCA0] block mb-2">
                Preferred day {selectedLocation.time_range && `(${selectedLocation.time_range})`}
              </Label>
              <div className="flex flex-wrap gap-2">
                {selectedLocation.available_days.map((d) => (
                  <button key={d} type="button" onClick={() => setPreferredDay(d)}
                    data-testid={`wizard-day-${d}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                      preferredDay === d
                        ? "bg-[#5ECCA0] text-[#14263D] border-[#5ECCA0]"
                        : "bg-[#14263D] text-white/70 border-white/15 hover:border-white/30"
                    }`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={submit} data-testid="appointment-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#5ECCA0]">Name</Label>
                <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name" className="mt-2 bg-[#14263D] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#5ECCA0]" />
              </div>
              <div>
                <Label className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#5ECCA0]">Phone</Label>
                <Input data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 …" className="mt-2 bg-[#14263D] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#5ECCA0]" />
              </div>
            </div>
            <div className="mt-5">
              <Label className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#5ECCA0]">Note (optional)</Label>
              <Textarea data-testid="form-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Anything you'd like the team to know" rows={3}
                className="mt-2 bg-[#14263D] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#5ECCA0] resize-none" />
            </div>
            <div className="mt-5">
              <Label className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#5ECCA0]">
                Prescription / Report (optional)
              </Label>
              {!documentName ? (
                <label className="mt-2 flex items-center gap-2 border border-dashed border-white/25 rounded-lg px-4 py-3 text-white/60 text-sm cursor-pointer hover:border-[#5ECCA0] transition-colors">
                  <Paperclip size={15} />
                  <span>Attach a photo or PDF — e.g. a previous prescription or scan report</span>
                  <input type="file" accept="image/*,application/pdf" onChange={onDocumentChange}
                    data-testid="form-document" className="hidden" />
                </label>
              ) : (
                <div className="mt-2 flex items-center justify-between border border-white/15 rounded-lg px-4 py-3 bg-[#14263D]">
                  <span className="text-white/80 text-sm truncate">{documentName}</span>
                  <button type="button" onClick={clearDocument} className="text-white/40 hover:text-red-400 ml-3 flex-shrink-0">
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
            <button type="submit" disabled={loading} data-testid="form-submit"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#5ECCA0] text-[#14263D] font-medium py-3.5 hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25 disabled:opacity-60">
              {loading ? <><Loader2 size={17} className="animate-spin" /> Sending…</> : "Request Appointment"}
            </button>
            <p className="text-center text-xs text-white/40 mt-4 font-mono-ui">
              Your details are kept private and used only to arrange your consultation.
            </p>
          </form>
        </StepShell>
      )}
    </div>
  );
};

export default BookingWizard;
